/**
 * Fleet client — routes REST + SSE calls either to this process (single-agent
 * mode, when no fleet config is present) or to a named peer via the FastAPI
 * `/api/fleet/<peer>/<path>` reverse-proxy (federated mode).
 *
 * Design intent (matches plans/04 Q1):
 *   - Single backend container hosts the React UI and the proxy. The browser
 *     only ever talks to one origin — no CORS surface.
 *   - When `selectedAgentId` is null/empty, calls go to `/api/<path>` (the
 *     facade routes Sassy shipped in Step 1). This keeps the single-agent
 *     deployment unchanged.
 *   - When `selectedAgentId` is set, calls go to `/api/fleet/<id>/<path>`,
 *     which proxies to the peer's `<baseUrl>/api/<path>` with the peer's
 *     bearer injected server-side.
 */

import { Agent } from "../types";
import { appendAuthToken } from "./firebaseAuth";

/** Trim leading slashes on the path so we can always join with a single `/`. */
function _normalize(path: string): string {
  if (!path) return "";
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Build a REST URL scoped to the selected agent. Pass `null` to target the
 * local process (single-agent mode); pass an id to route through the proxy.
 *
 * Example:
 *   agentScopedUrl(null,     "/agents")  → "/api/agents"
 *   agentScopedUrl("margo",  "/agents")  → "/api/fleet/margo/agents"
 */
export function agentScopedUrl(agentId: string | null, path: string): string {
  const p = _normalize(path);
  if (!agentId) return `/api${p}`;
  return `/api/fleet/${encodeURIComponent(agentId)}${p}`;
}

/**
 * EventSource flavored equivalent of agentScopedUrl. Same routing rules; the
 * server proxy streams `text/event-stream` responses back unchanged so the
 * native EventSource handles reconnection.
 */
export function agentScopedEventSource(agentId: string | null, path: string): EventSource {
  // Attach the cached Firebase ID token as a `?token=` query param. EventSource
  // can't set an `Authorization: Bearer` header, so without this the live
  // channel (/api/events, /api/timeline/stream) 401s over the public Funnel /
  // any Tailscale-proxied origin and the freshness badge falls to "stale".
  // No-op on LAN/Tailnet (no token cached → backend trusted-local bypass).
  return new EventSource(appendAuthToken(agentScopedUrl(agentId, path)));
}

/**
 * Fetch the federation roster.
 *
 * Order of fallback:
 *   1. `GET /api/fleet/agents`      → fleet config present (multi-agent mode)
 *   2. `GET /api/agents`            → single-agent fallback (the existing
 *                                     facade endpoint Sassy shipped Step 1)
 *
 * A 404 on the fleet endpoint means the FastAPI hasn't been updated with the
 * federation router yet — common during a partial rollout. Any other error
 * surfaces as an empty array; the caller decides whether to retry.
 *
 * The fleet endpoint returns `{id, name, baseUrl}` per peer (no avatar/role/
 * status — those live on each peer's own `/api/agents`). To render the
 * sidebar with real status we still need the per-agent details, which the
 * caller fetches by calling `agentScopedUrl(peer.id, "/agents")` for each
 * peer in parallel. This function just returns the roster shape.
 */
// Fetch the local agent (the FastAPI process itself, not a peer). Stamped
// with _localOnly so the router knows to send URLs to /api/X rather than
// /api/fleet/<id>/X — there's no proxy for self.
async function _fetchSelf(): Promise<Agent[]> {
  try {
    const res = await fetch("/api/agents");
    if (!res.ok) return [];
    const data = await res.json();
    if (!Array.isArray(data)) return [];
    return (data as Agent[]).map((a) => ({ ...a, _localOnly: true }));
  } catch {
    return [];
  }
}

export async function fetchFleet(): Promise<Agent[]> {
  try {
    const res = await fetch("/api/fleet/agents");
    if (res.ok) {
      const peers: Array<{ id: string; name: string; baseUrl: string }> = await res.json();
      if (Array.isArray(peers) && peers.length > 0) {
        // Fan out to each peer's own /api/agents through the proxy. Returns
        // the peer's full agent record (status, channels, uptime). Failures
        // gracefully fall back to a synthesized minimal record so a single
        // unreachable peer doesn't blank the sidebar for everyone.
        // Per-peer timeout — without this, a single slow/unreachable peer
        // blocks the whole sidebar load on Promise.all. 3 s is generous
        // for a healthy LAN/tailnet peer and short enough that operators
        // don't notice a stall when one peer is down.
        const enriched = await Promise.all(
          peers.map(async (p): Promise<Agent> => {
            try {
              const ctrl = new AbortController();
              const timer = setTimeout(() => ctrl.abort(), 3000);
              const r = await fetch(`/api/fleet/${encodeURIComponent(p.id)}/agents`, {
                signal: ctrl.signal,
              }).finally(() => clearTimeout(timer));
              if (r.ok) {
                const arr = await r.json();
                if (Array.isArray(arr) && arr[0]) {
                  // Force id + name back to the fleet-config values so the
                  // operator's labels always match agents.json, regardless
                  // of what the peer chose to call itself locally.
                  return { ...(arr[0] as Agent), id: p.id, name: p.name };
                }
              }
            } catch {
              // Peer unreachable or timed out — fall through to the
              // minimal synthesized record. Sidebar still shows the agent
              // (so the operator sees it's configured) but the status pill
              // goes offline so they know the peer's not responding.
            }
            return {
              id: p.id,
              name: p.name,
              avatar: (p.name?.[0] || "A").toUpperCase(),
              status: "offline",
              role: "unreachable",
              homeDir: p.baseUrl,
              uptime: "—",
              memoryUsage: "—",
              lastActive: new Date().toISOString(),
              channels: [],
            };
          })
        );
        // Prepend the LOCAL agent to the list so the operator's "self"
        // is always available as a scope option alongside peers. Without
        // this the dropdown only shows peers and the operator can't
        // pick "talk to my own agent." Self routes direct (_localOnly).
        const self = await _fetchSelf();
        return [...self, ...enriched];
      }
      // Empty fleet config → fall through to single-agent path.
    } else if (res.status !== 404) {
      // Non-404 non-200 — log and fall through. We don't want a 5xx on the
      // fleet endpoint to wipe the sidebar if the single-agent fallback
      // works.
      console.warn(`fleetClient: /api/fleet/agents returned ${res.status}`);
    }
  } catch (err) {
    console.warn("fleetClient: /api/fleet/agents fetch failed", err);
  }

  // Single-agent fallback: no fleet config present (404 or empty list).
  // Just return self — _localOnly stamped so routing skips the proxy.
  return _fetchSelf();
}
